import React, { useEffect, useState } from "react";
import { Button, Col, Container, FloatingLabel, Form, Row, Spinner, Stack } from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
  fetchBeds,
  fetchEthnicities,
  fetchGenders,
  fetchJobs,
  fetchPrograms,
  fetchRaces,
  fetchRelationships,
  fetchRooms,
  patchBed,
  postGuardian,
  postMember,
  postNote,
  patchGuardian,
  patchMember,
  fetchFields,
  postFieldValue,
} from "../../../../services/httpClient";
import DatePicker from "../../../ui/DatePicker";
import Input from "../../../ui/Input";
import InputPhone from "../../../ui/InputPhone";
import SelectObject from "../../../ui/SelectObject";
import SelectObjectTypeahead from "../../../ui/SelectObjectTypeahead";
import HouseholdCondensedCard from "./HouseholdCondensedCard";
import { User } from "../../../../types/user";
import FormCheck from "../../../ui/FormCheckbox";
import useApp from "../../../../utils/useApp";
import CustomField from "../../../ui/CustomField";

interface CreateMemberState {
  fetchHousehold: any;
  startingGuardians: any;
  startingHousehold: any;
  startingChildren: any;
  onClickDoneEdit: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  setUserToSave?: any;
}

const CreateMember = ({
  fetchHousehold,
  startingGuardians,
  startingHousehold,
  startingChildren,
  onClickDoneEdit,
  setShowToast,
  setToastBody,
  setToastText,
  setUserToSave,
}: CreateMemberState) => {
  const { id } = useParams();
  const { auth }: any = useApp();
  const [guardians, setGuardians] = useState<User[]>(startingGuardians);
  const [household, setHousehold] = useState<User[]>(startingHousehold);
  const [children, setChildren] = useState<User[]>(startingChildren);
  const heading = "Create Household Member";
  const [type, setType] = useState<string>("");
  const [firstName, setFirstName] = useState<string | undefined>("");
  const [lastName, setLastName] = useState<string>("");
  const [preferredName, setPreferredName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<number | null>(null);
  const [relationship, setRelationship] = useState<string>("");
  const [intakeDate, setIntakeDate] = useState<string>();
  const [intakeNote, setIntakeNote] = useState<string>("");
  const [noteDate, setNoteDate] = useState<string>("");
  const [birthDate, setBirthDate] = useState<string>();
  const [school, setSchool] = useState<string>("");
  const [grade, setGrade] = useState<string>("");
  const [gender, setGender] = useState<string>("");
  const [race, setRace] = useState<string>("");
  const [ethnicity, setEthnicity] = useState<string>("");
  const [program, setProgram] = useState<any>();
  const [level, setLevel] = useState<any>(null);
  const [job, setJob] = useState<string>("");
  const [room, setRoom] = useState<any>();
  const [bed, setBed] = useState<any>();
  const [custom, setCustom] = useState<any[]>([]);
  const [appAccess, setAppAccess] = useState<boolean>(false);
  const [saveIsEnabled, setSaveIsEnabled] = useState<boolean>(false);
  const [isValidEmail, setIsValidEmail] = useState<boolean>(true);
  const [isValidPhone, setIsValidPhone] = useState<boolean>(true);
  const [errMsg, setErrMsg] = useState("");
  const [relationships, setRelationships] = useState<any[]>([]);
  const [allRelationships, setAllRelationships] = useState<any[]>([]);
  const [genders, setGenders] = useState<any[]>([]);
  const [races, setRaces] = useState<any[]>([]);
  const [ethnicities, setEthnicities] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [beds, setBeds] = useState<any[]>([]);
  const [filteredBeds, setFilteredBeds] = useState<any[]>([]);
  const [customFields, setCustomFields] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  var types: any[] = [
    { name: "Primary Adult", key: "primary_adult" },
    { name: "Child", key: "child" },
    { name: "Other", key: "other" },
  ];

  useEffect(() => {
    getRelationships();
    getGenders();
    getRaces();
    getEthnicities();
    getPrograms();
    getJobs();

    if (auth?.profile.includeHMIS === 1) {
      getBeds();
    }

    getCustomFields();
    setIntakeDate(new Date().toISOString().split("T")[0]);
    setNoteDate(new Date().toISOString().split("T")[0]);
  }, []);

  const getRelationships = () => {
    fetchRelationships().then((res: any) => {
      setAllRelationships(res[0].data);
      setRelationships(
        res[0].data.filter((c: any) => {
          return c.key !== "child";
        })
      );
    });
  };

  const getGenders = () => {
    fetchGenders().then((res: any) => {
      setGenders(res[0].data);
    });
  };

  const getRaces = () => {
    fetchRaces().then((res: any) => {
      setRaces(res[0].data);
    });
  };

  const getEthnicities = () => {
    fetchEthnicities().then((res: any) => {
      setEthnicities(res[0].data);
    });
  };

  const getPrograms = () => {
    fetchPrograms().then((res: any) => {
      const programs = res[0].data;
      const updatedPrograms = [{ id: 0, key: 0, name: "Select from List", programLevels: [] }, ...programs];

      setPrograms(updatedPrograms);
    });
  };

  const getJobs = () => {
    fetchJobs().then((res: any) => {
      const jobs = res[0].data;
      const availJobs = jobs.filter((j: any) => j.positions_filled < j.positions_total);
      setJobs(availJobs);
    });
  };

  const getBeds = () => {
    fetchBeds()
      .then((res: any) => {
        var bedList = res[0].data;
        //filter to vacant beds
        bedList = bedList.filter((b: any) => b.occupant_id === null);

        bedList.sort((a: any, b: any) => {
          // group by room name
          if (a.room_name !== b.room_name) {
            return a.room_name.localeCompare(b.room_name, undefined, { numeric: true, sensitivity: "base" });
          }
          // sort beds in each room alphanumerically
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });

        setBeds(bedList);
        setFilteredBeds(bedList);
        getRooms();
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setErrMsg("");
      });
  };

  const getRooms = () => {
    fetchRooms()
      .then((res: any) => {
        const roomList = res[0].data;
        roomList.sort((a: any, b: any) => {
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });
        setRooms(roomList);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const getCustomFields = async () => {
    await fetchFields().then((res: any) => {
      setCustomFields(res[0].data);
    });
  };

  const updateType = (e: any) => {
    setType(e.target.value);
    setAppAccess(false);
    if (e.target.value === "child") {
      setRelationship("child");
    }
    if (setUserToSave) setUserToSave(true);
  };

  function enableSave() {
    if (firstName && lastName) {
      if (appAccess) {
        if (isValidEmail && isValidPhone) {
          setSaveIsEnabled(true);
        } else {
          setSaveIsEnabled(false);
        }
      } else {
        setSaveIsEnabled(true);
      }
    } else {
      setSaveIsEnabled(false);
    }
  }

  const updateFirstName = (e: any) => {
    setFirstName(e.target.value);

    if (e.target.value && lastName) {
      if (appAccess) {
        if (isValidEmail && isValidPhone) {
          setSaveIsEnabled(true);
        } else {
          setSaveIsEnabled(false);
        }
      } else {
        setSaveIsEnabled(true);
      }
    } else {
      setSaveIsEnabled(false);
    }
    if (setUserToSave) setUserToSave(true);
  };

  const updateLastName = (e: any) => {
    setLastName(e.target.value);

    if (e.target.value && firstName) {
      if (appAccess) {
        if (isValidEmail && isValidPhone) {
          setSaveIsEnabled(true);
        } else {
          setSaveIsEnabled(false);
        }
      } else {
        setSaveIsEnabled(true);
      }
    } else {
      setSaveIsEnabled(false);
    }
    if (setUserToSave) setUserToSave(true);
  };

  function validateEmail(email: string, access: boolean) {
    var regexp = /\S+@\S+\.\S+/;
    if (access) {
      return regexp.test(email);
    } else {
      return regexp.test(email) || email.length === 0;
    }
  }

  function validatePhone(phoneNum: string, access: boolean) {
    let length = phoneNum.length;
    if (access) {
      return length === 11;
    } else {
      return length === 11 || length === 1;
    }
  }

  const updateEmail = (e: any) => {
    setEmail(e.target.value);
    let validEmail = validateEmail(e.target.value, appAccess);

    if (validEmail) {
      setIsValidEmail(true);
      if (appAccess) {
        if (isValidPhone) {
          if (phone && firstName && lastName) {
            setSaveIsEnabled(true);
          } else {
            setSaveIsEnabled(false);
          }
        }
      } else {
        if (firstName && lastName) {
          if (validEmail && isValidPhone) {
            setSaveIsEnabled(true);
          } else {
            setSaveIsEnabled(false);
          }
        }
      }
    } else {
      setIsValidEmail(false);
      setSaveIsEnabled(false);
    }
    if (setUserToSave) setUserToSave(true);
  };

  const updatePhone = (e: any) => {
    setPhone(e);
    let validPhone = validatePhone(e, appAccess);

    if (validPhone) {
      setIsValidPhone(true);

      if (type === "primary_adult") {
        if (appAccess) {
          if (isValidEmail) {
            if (email && firstName && lastName) {
              setSaveIsEnabled(true);
            } else {
              setSaveIsEnabled(false);
            }
          }
        } else {
          if (firstName && lastName) {
            if (isValidEmail && validPhone) {
              setSaveIsEnabled(true);
            } else {
              setSaveIsEnabled(false);
            }
          }
        }
      } else {
        setSaveIsEnabled(true);
      }
    } else {
      setIsValidPhone(false);
      setSaveIsEnabled(false);
    }
    if (setUserToSave) setUserToSave(true);
  };

  const updateAppAccess = () => {
    if (!appAccess) {
      const validEmail = validateEmail(email, true);
      const validPhone = validatePhone(phone ? phone.toString() : "", true);
      setIsValidEmail(validEmail);
      setIsValidPhone(validPhone);

      if (firstName && lastName && email && phone) {
        if (validEmail && validPhone) {
          setSaveIsEnabled(true);
        } else {
          setSaveIsEnabled(false);
        }
      } else {
        setSaveIsEnabled(false);
      }
    } else {
      if (firstName && lastName) {
        const validEmail = validateEmail(email, false);
        const validPhone = validatePhone(phone ? phone.toString() : "", false);
        setIsValidEmail(validEmail);
        setIsValidPhone(validPhone);

        if (validEmail && validPhone) {
          setSaveIsEnabled(true);
        } else {
          setSaveIsEnabled(false);
        }
      } else {
        setSaveIsEnabled(false);
      }
    }

    setAppAccess(!appAccess);
    if (setUserToSave) setUserToSave(true);
  };

  const updatePreferredName = (e: any) => {
    setPreferredName(e.target.value);
    if (setUserToSave) setUserToSave(true);
  };

  const updateRelationship = (e: any) => {
    setRelationship(e.target.value);
    if (setUserToSave) setUserToSave(true);
  };

  const updateSchool = (e: any) => {
    setSchool(e.target.value);
    if (setUserToSave) setUserToSave(true);
  };

  const updateGrade = (e: any) => {
    setGrade(e.target.value);
    if (setUserToSave) setUserToSave(true);
  };

  const updateGender = (e: any) => {
    setGender(e.target.value);
    if (setUserToSave) setUserToSave(true);
  };

  const updateRace = (e: any) => {
    setRace(e.target.value);
    if (setUserToSave) setUserToSave(true);
  };

  const updateEthnicity = (e: any) => {
    setEthnicity(e.target.value);
    if (setUserToSave) setUserToSave(true);
  };

  const updateProgram = (e: any) => {
    setLevel(null);
    setLevels([]);
    if (e.programLevels && e.programLevels.length > 0) {
      setLevels(e.programLevels);
    }
    setProgram(e);
    if (setUserToSave) setUserToSave(true);
  };

  const updateLevel = (e: any) => {
    setLevel(e);
    if (setUserToSave) setUserToSave(true);
  };

  useEffect(() => {
    if (program && levels?.length > 0 && !level) {
      setSaveIsEnabled(false);
    } else {
      enableSave();
    }
  }, [program, level]);

  const updateJob = (e: any) => {
    setJob(e.target.value);
    if (setUserToSave) setUserToSave(true);
  };

  const updateRoom = (e: any) => {
    setRoom(e.target.value);
    setBed(null);
    const filtered = beds.filter((b: any) => b.room_key === e.target.value);
    setFilteredBeds(filtered);
    if (setUserToSave) setUserToSave(true);
  };

  const updateBed = (e: any) => {
    setBed(e.target.value);
    if (setUserToSave) setUserToSave(true);
  };

  const updateIntakeNote = (e: any) => {
    setIntakeNote(e.target.value);
    if (setUserToSave) setUserToSave(true);
  };

  const onClickDoneCreate = () => {
    fetchHousehold();
    onClickDoneEdit();
  };

  const saveMember = (isDone: boolean) => {
    setErrMsg("");

    const cleanProgram = program?.key;

    if (type === "primary_adult") {
      if (isValidEmail && isValidPhone) {
        const updatedGuardian: any = {
          role: "family",
          appaccess: appAccess,
          firstname: firstName,
          lastname: lastName,
          preferredname: preferredName,
          email: email,
          phonenumber: phone == 1 ? null : phone?.toString(),
          relationship: relationship,
          birthdate: birthDate,
          gender: gender,
          race: race,
          ethnicity: ethnicity,
          program: cleanProgram ?? null,
          level_id: level?.id ? level.id : null,
          job: job,
          is_guardian: true,
          is_new: true,
        };

        setIsSaving(true);
        //create new guardian
        postGuardian(updatedGuardian, id)
          .then((res: any) => {
            const userId = res[0].data.user_id;
            setSaveIsEnabled(false);
            setGuardians((prev) => [...prev, updatedGuardian]);
            clearMember();
            setShowToast(true);
            setToastText("Primary Adult Created");
            const toast = preferredName ? firstName + " '" + preferredName + "' " + lastName : firstName + " " + lastName;
            setToastBody(toast);

            //update / create custom field values
            custom.map((cust: any) => {
              //post
              const fieldVal = {
                field_id: cust?.fieldId,
                person_id: userId,
                person_type: "guardian",
                value: cust?.value,
              };

              postFieldValue(fieldVal).catch((err: any) => {
                console.log(err);
                setErrMsg(err?.response?.data?.msg);
              });
            });

            const note = {
              notetype: "intake",
              subject: {
                id: userId,
                type: "guardian",
              },
              fields: {
                description: intakeNote,
                date: noteDate,
              },
            };

            //save intake note
            postNote(note, id)
              .then((res: any) => {
                const noteId = res[0].data.note_id;

                //update user intake data
                const intakeData = {
                  intake_at: intakeDate,
                  intake_note_id: noteId,
                };

                patchGuardian(intakeData, id, userId).catch((err: any) => {
                  console.log(err);
                });
              })
              .catch((err: any) => {
                setErrMsg(err?.response?.data?.msg || "Failed to save note");
              });

            if (auth?.profile.includeHMIS === 1) {
              if (bed) {
                const updatedBed = {
                  occupant_id: userId,
                  occupant_type: "guardian",
                };

                patchBed(updatedBed, bed)
                  .then((res: any) => {})
                  .catch((err: any) => {
                    setErrMsg(err?.response?.data?.msg);
                  });
              }
            }
          })
          .catch((err: any) => {
            setErrMsg(err?.response?.data?.msg);
            setIsSaving(false);
          })
          .finally(() => {
            if (isDone) {
              fetchHousehold();
              onClickDoneEdit(true);
            } else {
              getJobs();
            }
            setIsSaving(false);
          });
      } else {
        setErrMsg("Email or Phone is in Invalid Format");
      }
    } else if (type === "other" || type === "child") {
      if (isValidPhone) {
        const updatedMember: any = {
          role: "",
          firstname: firstName,
          lastname: lastName,
          preferredname: preferredName,
          phonenumber: phone?.toString(),
          relationship: relationship,
          birthdate: birthDate,
          gender: gender,
          race: race,
          ethnicity: ethnicity,
          program: cleanProgram ?? null,
          level_id: level?.id ? level.id : null,
          job: job,
          school: school,
          grade: grade,
          is_new: true,
        };

        setIsSaving(true);
        //create new member
        postMember(updatedMember, id)
          .then((res: any) => {
            const memberId = res[0].data.member_id;

            setSaveIsEnabled(false);
            if (type === "other") {
              setHousehold((prev) => [...prev, updatedMember]);
            } else {
              setChildren((prev) => [...prev, updatedMember]);
            }
            clearMember();
            setShowToast(true);
            setToastText("Member Created");
            const toast = preferredName ? firstName + " '" + preferredName + "' " + lastName : firstName + " " + lastName;
            setToastBody(toast);

            //update / create custom field values
            custom.map((cust: any) => {
              //post
              const fieldVal = {
                field_id: cust?.fieldId,
                person_id: memberId,
                person_type: "member",
                value: cust?.value,
              };

              postFieldValue(fieldVal).catch((err: any) => {
                console.log(err);
                setErrMsg(err?.response?.data?.msg);
              });
            });

            const note = {
              notetype: "intake",
              subject: {
                id: memberId,
                type: "member",
              },
              fields: {
                description: intakeNote,
                date: noteDate,
              },
            };

            //save intake note
            postNote(note, id)
              .then((res: any) => {
                const noteId = res[0].data.note_id;

                //update user intake data
                const intakeData = {
                  intake_at: intakeDate,
                  intake_note_id: noteId,
                };

                patchMember(intakeData, id, memberId).catch((err: any) => {
                  console.log(err);
                });
              })
              .catch((err: any) => {
                setErrMsg(err?.response?.data?.msg || "Failed to save note");
              });

            if (auth?.profile.includeHMIS === 1) {
              if (bed) {
                const updatedBed = {
                  occupant_id: memberId,
                  occupant_type: "member",
                };

                patchBed(updatedBed, bed)
                  .then((res: any) => {})
                  .catch((err: any) => {
                    setErrMsg(err?.response?.data?.msg);
                  });
              }
            }
          })
          .catch((err: any) => {
            setErrMsg(err?.response?.data?.msg);
            setIsSaving(false);
          })
          .finally(() => {
            if (isDone) {
              fetchHousehold();
              onClickDoneEdit(true);
            } else {
              getJobs();
            }
            setIsSaving(false);
          });
      } else {
        setErrMsg("Phone is in Invalid Format");
      }
    }
  };

  const clearMember = () => {
    setType("");
    setFirstName("");
    setLastName("");
    setPreferredName("");
    setEmail("");
    setPhone(null);
    setRelationship("");
    setBirthDate("");
    setSchool("");
    setGrade("");
    setGender("");
    setRace("");
    setEthnicity("");
    setProgram("");
    setJob("");
    setRoom("");
    setBed("");
    setAppAccess(false);
    setIntakeNote("");
    setIntakeDate(new Date().toISOString().split("T")[0]);
    setNoteDate(new Date().toISOString().split("T")[0]);
    if (setUserToSave) setUserToSave(false);
  };

  return (
    <>
      <div>
        <Stack gap={3}>
          <Row>
            <Col>
              <h4 className="app-header-text">{heading}</h4>
            </Col>
            <Col className="text-end">
              <Stack className="stack-float" direction="horizontal" gap={3}>
                <Button className="app-primary-color button" onClick={() => onClickDoneCreate()}>
                  Done
                </Button>
              </Stack>
            </Col>
          </Row>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <Row>
            <Col xs={12} sm={12} md={12} lg={8} xl={6}>
              <Row className="mb-3">
                <Col xs={12}>
                  <SelectObject label="Household Member Type" value={type} options={types} onUpdate={(e: any) => updateType(e)} />
                </Col>
              </Row>
              <Stack gap={3}>
                <>
                  {(type === "primary_adult" || type === "other" || type === "child") && (
                    <>
                      <Row>
                        <Col xs={6}>
                          <Input
                            label="First Name"
                            type="text"
                            value={firstName}
                            onUpdate={(e: any) => updateFirstName(e)}
                            required={true}
                          />
                        </Col>
                        <Col xs={6}>
                          <Input label="Last Name" type="text" value={lastName} onUpdate={(e: any) => updateLastName(e)} required={true} />
                        </Col>
                      </Row>
                      <Row>
                        {type === "primary_adult" && (
                          <Col xs={6}>
                            <Input label="Email" type="text" value={email} onUpdate={(e: any) => updateEmail(e)} required={appAccess} />
                          </Col>
                        )}
                        <Col xs={6}>
                          <InputPhone
                            label="Phone"
                            value={phone}
                            onUpdate={(e: any) => updatePhone(e)}
                            required={type === "primary_adult" ? appAccess : false}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={6}>
                          <Input label="Preferred Name" type="text" value={preferredName} onUpdate={(e: any) => updatePreferredName(e)} />
                        </Col>
                        <Col xs={6}>
                          <DatePicker label="Birth Date" currentValue={birthDate} setCurrentValue={setBirthDate} />
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={6}>
                          <SelectObject
                            label="Relationship"
                            value={relationship}
                            options={type !== "primary_adult" ? allRelationships : relationships}
                            onUpdate={(e: any) => updateRelationship(e)}
                          />
                        </Col>
                        <Col xs={6}>
                          <SelectObject label="Gender" value={gender} options={genders} onUpdate={(e: any) => updateGender(e)} />
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={6}>
                          <SelectObject
                            label="Ethnicity"
                            value={ethnicity}
                            options={ethnicities}
                            onUpdate={(e: any) => updateEthnicity(e)}
                          />
                        </Col>
                        <Col xs={6}>
                          <SelectObject label="Race" value={race} options={races} onUpdate={(e: any) => updateRace(e)} />
                        </Col>
                      </Row>
                      {(programs.length > 0 || jobs.length > 0) && (
                        <Row>
                          {programs.length > 0 && (
                            <Col xs={6}>
                              <SelectObjectTypeahead
                                label="Program"
                                value={program}
                                options={programs}
                                onUpdate={(e: any) => updateProgram(e)}
                              />
                            </Col>
                          )}
                          {levels.length > 0 && (
                            <Col xs={6}>
                              <SelectObjectTypeahead
                                label="Program Level"
                                value={level}
                                options={levels}
                                onUpdate={(e: any) => updateLevel(e)}
                              />
                            </Col>
                          )}
                          {jobs.length > 0 && (
                            <Col xs={6} style={levels.length > 0 ? { marginTop: "1rem" } : {}}>
                              <SelectObject label="Job" value={job} options={jobs} onUpdate={(e: any) => updateJob(e)} />
                            </Col>
                          )}
                        </Row>
                      )}
                      {type === "child" && (
                        <Row>
                          <Col xs={6}>
                            <Input label="School" type="text" value={school} onUpdate={(e: any) => updateSchool(e)} />
                          </Col>
                          <Col xs={4}>
                            <Input label="Grade" type="text" value={grade} onUpdate={(e: any) => updateGrade(e)} />
                          </Col>
                        </Row>
                      )}
                      {rooms.length > 0 && beds.length > 0 && (
                        <Row>
                          <Col xs={6}>
                            <SelectObject label="Room" value={room} options={rooms} onUpdate={(e: any) => updateRoom(e)} />
                          </Col>
                          <Col xs={6}>
                            <SelectObject
                              label="Bed"
                              value={bed}
                              options={filteredBeds}
                              onUpdate={(e: any) => updateBed(e)}
                              disabled={room ? false : true}
                              initialOption="Vacant"
                            />
                          </Col>
                        </Row>
                      )}
                      {customFields?.length > 0 && (
                        <Row className="h-auto">
                          {customFields?.map((custom: any) => (
                            <CustomField
                              fieldId={custom?.id}
                              label={custom?.label}
                              type={custom?.type}
                              val={custom?.value}
                              setCustom={setCustom}
                              columns={6}
                            />
                          ))}
                        </Row>
                      )}
                      <Row>
                        {type === "primary_adult" && (
                          <Col xs={6} className="mt-2 mb-0">
                            <FormCheck label="Mobile App Access" value={appAccess} onUpdate={(e: any) => updateAppAccess()} />
                          </Col>
                        )}
                      </Row>
                      <h4 className="app-header-text mb-0">Intake Note</h4>
                      <FloatingLabel controlId="floatingInput" label="Note">
                        <Form.Control
                          as="textarea"
                          type="textarea"
                          placeholder="Note"
                          style={{ height: "auto" }}
                          value={intakeNote}
                          onChange={(e) => updateIntakeNote(e)}
                          rows={3}
                        />
                      </FloatingLabel>
                      <Row>
                        <Col xs={6}>
                          <DatePicker label="Note Date" currentValue={noteDate} setCurrentValue={setNoteDate} hasMaxDate={true} />
                        </Col>
                        <Col xs={6}>
                          <DatePicker label="Intake Date" currentValue={intakeDate} setCurrentValue={setIntakeDate} hasMaxDate={true} />
                        </Col>
                      </Row>
                    </>
                  )}
                </>
              </Stack>
            </Col>
            <Col xs={12} sm={12} md={12} lg={8} xl={6}>
              {isSaving ? (
                <Container>
                  <Button size="sm" className="spinner-button spinner-button-main mb-5">
                    <Spinner animation="border" style={{ color: "#F6893D" }} />
                  </Button>
                </Container>
              ) : (
                <Row className="flex-wrap">
                  {guardians.map((u: User) => (
                    <Col key={u.id} xs={6} className="pb-3 d-flex">
                      <HouseholdCondensedCard user={u} />
                    </Col>
                  ))}
                  {household.map((u: User) => (
                    <Col key={u.id} xs={6} className="pb-3 h-100">
                      <HouseholdCondensedCard user={u} />
                    </Col>
                  ))}
                  {children.map((u: User) => (
                    <Col key={u.id} xs={6} className="pb-3 h-100 d-flex">
                      <HouseholdCondensedCard user={u} />
                    </Col>
                  ))}
                </Row>
              )}
            </Col>
          </Row>
          {(type === "primary_adult" || type === "other" || type === "child") && (
            <Row className="pt-0 pb-5">
              <Stack className="stack-float" direction="horizontal" gap={3}>
                <Button className="app-secondary-color button" onClick={() => saveMember(false)} disabled={!saveIsEnabled}>
                  Save and Add Another
                </Button>
                <div className="vr" />
                <Button className="app-secondary-color button" onClick={() => saveMember(true)} disabled={!saveIsEnabled}>
                  Save and Done
                </Button>
              </Stack>
            </Row>
          )}
        </Stack>
      </div>
    </>
  );
};

export default CreateMember;
